import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Cpu, HardDrive, Zap, Activity } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { productionLogger } from '@/utils/production-logger';
import { TIMING } from '@/constants';

export function SystemResourcesWidget() {
  const [stats, setStats] = useState({
    cpuUsage: 0,
    memoryUsage: 0,
    diskUsage: 0,
    uptime: 0,
    loading: true,
    error: null as string | null,
    // Add actual system specs
    totalMemoryGB: 16,
    cpuSpeedGHz: 3.2,
    totalStorageGB: 500
  });

  useEffect(() => {
    const fetchSystemStats = async () => {
      try {
        // Enhanced Security: Check rate limiting before proceeding
        const { data: rateLimitCheck, error: rateLimitError } = await supabase
          .rpc('check_admin_dashboard_rate_limit');
          
        if (rateLimitError) {
          throw new Error(`Rate limit check failed: ${rateLimitError.message}`);
        }
        
        if (!rateLimitCheck) {
          throw new Error('Rate limit exceeded for system resources widget access');
        }

        // Enhanced Security: Log widget access for audit trail
        const { error: logError } = await supabase
          .rpc('log_dashboard_widget_access', {
            p_widget_type: 'system_resources',
            p_data_points: 1
          });
          
        if (logError) {
          productionLogger.warn('Failed to log system resources widget access:', logError);
          // Continue despite logging failure - don't block widget functionality
        }

        // Simulate system resource monitoring
        // In a real app, this would come from monitoring APIs
        const cpuUsage = Math.floor(Math.random() * 30) + 15; // 15-45%
        const memoryUsage = Math.floor(Math.random() * 40) + 30; // 30-70%
        const diskUsage = Math.floor(Math.random() * 20) + 25; // 25-45%
        
        // Calculate uptime since app started (mock)
        const startTime = new Date('2025-08-07').getTime();
        const uptime = Math.floor((Date.now() - startTime) / (1000 * 60 * 60 * 24));

        setStats({
          cpuUsage,
          memoryUsage,
          diskUsage,
          uptime,
          loading: false,
          error: null,
          totalMemoryGB: 16,
          cpuSpeedGHz: 3.2,
          totalStorageGB: 500
        });
      } catch (err) {
        productionLogger.error('Failed to fetch system stats:', err);
        setStats(prev => ({
          ...prev,
          loading: false,
          error: err instanceof Error && err.message.includes('Rate limit exceeded') 
            ? 'Rate limit exceeded - please wait before refreshing'
            : err instanceof Error && err.message.includes('Rate limit check failed')
            ? 'Security check failed - contact administrator'
            : 'Unable to load system data'
        }));
      }
    };

    fetchSystemStats();
    
    // Enhanced Security: Increased refresh to 60 seconds to respect rate limits
    const interval = setInterval(fetchSystemStats, TIMING.ADMIN_WIDGET_REFRESH);
    return () => clearInterval(interval);
  }, []);

  const getUsageColor = (usage: number) => {
    if (usage < 50) return 'text-primary';
    if (usage < 80) return 'text-accent';
    return 'text-destructive';
  };

  if (stats.loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Activity className="w-4 h-4" />
            System Resources
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            <div className="h-8 bg-muted rounded w-16"></div>
            <div className="h-4 bg-muted rounded w-24"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (stats.error) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Activity className="w-4 h-4 text-destructive" />
            System Resources
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-destructive text-sm">{stats.error}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="min-h-[140px]">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Activity className="w-4 h-4 text-secondary" />
          System Resources
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Activity className="w-6 h-6 text-secondary" />
            <div className="text-2xl font-bold text-accent">
              {stats.cpuSpeedGHz}GHz
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            CPU {stats.cpuUsage}% • RAM {Math.round(stats.memoryUsage * stats.totalMemoryGB / 100)}GB/{stats.totalMemoryGB}GB • Disk {Math.round(stats.diskUsage * stats.totalStorageGB / 100)}GB/{stats.totalStorageGB}GB
          </div>
          <div className="flex justify-between text-xs border-t pt-2">
            <span className="text-muted-foreground">Memory usage</span>
            <span className={`font-medium ${getUsageColor(stats.memoryUsage)}`}>
              {stats.memoryUsage}%
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}